/**
 * Whether this window shows the running presentation (Part 05 decision 9).
 * It gates the overlay drawing, the wall's `inert`, the presentation keys and
 * the Back pill, so Esc on one display never ends another display's focus.
 */
import { getWindowDisplayId } from "@/desktop/use-window-role";
import type { PresentationState } from "@/lib/focus/focus-types";
import { isTauri } from "@/lib/is-tauri";
import { useDesktopMode, type DesktopMode } from "@/stores/desktop-mode-store";
import { usePresentationStore } from "@/stores/presentation-store";

function focusVisibleHere(
  presentation: PresentationState | null,
  mode: DesktopMode,
  compactFrameOverlayIds: string[],
): boolean {
  if (!presentation) return false;
  const { displayId } = presentation;
  if (displayId != null && displayId !== getWindowDisplayId()) return false;
  if (!isTauri() || mode === "denk") return true;
  // Over the desktop (compact, peek) focus shows only its overlay frames.
  return (
    presentation.variant === "focus" &&
    compactFrameOverlayIds.some((id) => presentation.frameIds.includes(id))
  );
}

export function isFocusVisibleHere(): boolean {
  const { mode, compactFrameOverlayIds } = useDesktopMode.getState();
  return focusVisibleHere(
    usePresentationStore.getState().presentation,
    mode,
    compactFrameOverlayIds,
  );
}

export function useFocusVisibleHere(): boolean {
  const mode = useDesktopMode((state) => state.mode);
  const compactFrameOverlayIds = useDesktopMode(
    (state) => state.compactFrameOverlayIds,
  );
  return usePresentationStore((state) =>
    focusVisibleHere(state.presentation, mode, compactFrameOverlayIds),
  );
}
